import { StorageProvider } from './types';

/**
 * LocalStorage provider implementation
 */
export class LocalStorageProvider implements StorageProvider {
  /**
   * Get a value from localStorage
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) as T : null;
    } catch (error) {
      console.error('Error retrieving from localStorage:', error);
      return null;
    }
  }

  /**
   * Store a value in localStorage
   * @param key The key to store the value under
   * @param value The value to store
   */
  async put<T>(key: string, value: T): Promise<void> {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error storing in localStorage:', error);
      throw error;
    }
  }

  /**
   * Remove a value from localStorage
   * @param key The key to remove
   */
  async remove(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      throw error;
    }
  }

  /**
   * Check if a key exists in localStorage
   * @param key The key to check
   * @returns True if the key exists, false otherwise
   */
  async has(key: string): Promise<boolean> {
    return localStorage.getItem(key) !== null;
  }
  
  /**
   * Get the storage type name
   */
  getStorageType(): string {
    return 'localStorage';
  }
}